import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { ConditionDto } from "../../api/types";
import { previewAutoTransactionMatches } from "../../api/auto-transactions";
import { isClientError } from "../../api/common";
import { DialogCloseButton } from "../DialogCloseButton";
import { MatchRow } from "./MatchRow";

export type AutoTransactionMatchesDialogProps = {
  open: boolean;
  onClose: () => void;
  /**
   * The condition to evaluate. Captured at the time the dialog opens via
   * React Query's key; if the parent reopens the dialog after editing, the
   * new condition triggers a fresh fetch.
   */
  condition: ConditionDto;
};

/**
 * Shows the transactions that would match the given (uncommitted) rule
 * condition. Used by the auto-transaction edit form so the user can verify
 * the rule before saving.
 *
 * Fires the preview query only while open and only once per distinct
 * condition (React Query caches per key). The user is expected to close the
 * dialog before editing the rule further — there's no in-dialog refresh.
 */
export const AutoTransactionMatchesDialog = ({
  open,
  onClose,
  condition,
}: AutoTransactionMatchesDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const query = useQuery({
    queryKey: ["auto-transactions", "preview-matches", condition],
    queryFn: () => previewAutoTransactionMatches(condition),
    enabled: open,
    // Keep the result around briefly so reopening the dialog with the same
    // condition is instant.
    staleTime: 30_000,
    // A refused condition is refused again; show that now, not after three
    // backed-off retries.
    retry: (failureCount, error) => !isClientError(error) && failureCount < 3,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          sx: { display: "flex", flexDirection: "column", maxHeight: "90vh" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Box component="span">Matching transactions</Box>
        <DialogCloseButton
          onClick={onClose}
          size="large"
          sx={{ minWidth: 44, minHeight: 44 }}
        />
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {query.isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {query.isError && (
          <Alert severity="error" sx={{ m: 2 }}>
            {query.error instanceof Error
              ? query.error.message
              : "Failed to load matching transactions."}
          </Alert>
        )}
        {query.data && (
          <>
            {query.data.truncated && (
              <Alert severity="info" sx={{ m: 2 }} variant="outlined">
                Showing the first {query.data.matches.length} matches — more may
                exist further back in time.
              </Alert>
            )}
            {query.data.matches.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary" variant="body2">
                  No matches — this rule wouldn't apply to any transaction.
                </Typography>
              </Box>
            ) : (
              <Stack divider={null}>
                {query.data.matches.map((tx) => (
                  <MatchRow key={tx.id} transaction={tx} />
                ))}
              </Stack>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
